import {
  actionItemOrigin,
  actionItemSchema,
  ACTION_ITEM_ENTITY_TYPE,
} from "@brains/action-items";
import { actorRefKey, type ActorRef } from "@brains/contracts";
import type { ContentVisibility, EntityPluginContext } from "@brains/plugins";
import {
  decisionSchema,
  type DecisionEntity,
} from "../schemas/conversation-memory";
import { summarySchema, type SummaryEntity } from "../schemas/summary";
import { z } from "@brains/utils/zod";
import { SummaryAdapter } from "../adapters/summary-adapter";
import { DECISION_ENTITY_TYPE, SUMMARY_ENTITY_TYPE } from "./constants";
import { buildFallbackExcerpt } from "./excerpt";

/** The space a conversation or a memory belongs to, for same-space recall. */
function getConversationSpaceId(scope: {
  interfaceType: string;
  channelId: string;
}): string {
  return `${scope.interfaceType}:${scope.channelId}`;
}

const DEFAULT_MEMORY_LIMIT = 5;
const CANDIDATE_MULTIPLIER = 4;
const MEMORY_ENTITY_TYPES = [
  SUMMARY_ENTITY_TYPE,
  DECISION_ENTITY_TYPE,
  ACTION_ITEM_ENTITY_TYPE,
];
const MAX_AGENT_CONTEXT_CONTENT_LENGTH = 1600;
const MAX_SUMMARY_CONTEXT_ENTRIES = 3;
const MAX_SUMMARY_CONTEXT_KEY_POINTS = 5;
const summaryAdapter = new SummaryAdapter();

export const conversationMemorySearchEntitySchema: ReturnType<
  typeof z.union<
    [typeof summarySchema, typeof decisionSchema, typeof actionItemSchema]
  >
> = z.union([summarySchema, decisionSchema, actionItemSchema]);
type ConversationMemorySearchEntity = z.output<
  typeof conversationMemorySearchEntitySchema
>;

export interface RetrieveConversationMemoryInput {
  query?: string | undefined;
  conversationId?: string | undefined;
  interfaceType?: string | undefined;
  channelId?: string | undefined;
  limit?: number | undefined;
  includeOtherSpaces?: boolean | undefined;
  /** Explicit identity filter; does not cross spaces unless includeOtherSpaces is true. */
  identity?: ActorRef | undefined;
  /** Caller visibility scope; undefined fails closed in the entity service to public-only. */
  visibilityScope?: ContentVisibility | undefined;
}

export interface RetrievedConversationMemory {
  id: string;
  entityType: "summary" | "decision" | "action-item";
  conversationId: string;
  spaceId: string;
  channelId: string;
  channelName?: string;
  interfaceType: string;
  updated: string;
  visibility: ContentVisibility;
  score: number;
  excerpt: string;
  content: string;
  messageCount?: number;
  entryCount?: number;
  status?: string;
}

export interface RetrieveConversationMemoryResult {
  query: string;
  spaceId?: string;
  results: RetrievedConversationMemory[];
}

interface MemoryCandidate {
  entity: ConversationMemorySearchEntity;
  score: number;
  excerpt: string;
}

export class ConversationMemoryRetriever {
  private readonly context: EntityPluginContext;
  constructor(context: EntityPluginContext) {
    this.context = context;
  }

  public async retrieve(
    input: RetrieveConversationMemoryInput,
  ): Promise<RetrieveConversationMemoryResult> {
    const query = input.query?.trim() ?? "";
    const limit = Math.max(1, input.limit ?? DEFAULT_MEMORY_LIMIT);
    const spaceId = await this.resolveSpaceId(input);
    const candidates = await this.loadCandidates(
      query,
      limit,
      input.visibilityScope,
    );

    const scopedCandidates = candidates
      // Action items tracked from the Inbox have no conversation to recall.
      .filter(
        (candidate) => this.conversationOf(candidate.entity) !== undefined,
      )
      .filter((candidate) => {
        if (!spaceId || input.includeOtherSpaces) return true;
        return this.getEntitySpaceId(candidate.entity) === spaceId;
      })
      .filter((candidate) => this.matchesIdentity(candidate.entity, input));

    const seen = new Set<string>();
    const ranked = scopedCandidates
      .sort((left, right) => {
        const leftSameSpace = spaceId
          ? this.getEntitySpaceId(left.entity) === spaceId
          : false;
        const rightSameSpace = spaceId
          ? this.getEntitySpaceId(right.entity) === spaceId
          : false;
        if (leftSameSpace !== rightSameSpace) return leftSameSpace ? -1 : 1;
        if (right.score !== left.score) return right.score - left.score;
        return (
          Date.parse(right.entity.updated) - Date.parse(left.entity.updated)
        );
      })
      .filter((candidate) => {
        if (seen.has(candidate.entity.id)) return false;
        seen.add(candidate.entity.id);
        return true;
      })
      .slice(0, limit)
      .map((candidate) => this.toMemory(candidate));

    return {
      query,
      ...(spaceId ? { spaceId } : {}),
      results: ranked,
    };
  }

  private async resolveSpaceId(
    input: RetrieveConversationMemoryInput,
  ): Promise<string | undefined> {
    if (input.interfaceType && input.channelId) {
      return getConversationSpaceId({
        interfaceType: input.interfaceType,
        channelId: input.channelId,
      });
    }

    if (!input.conversationId) return undefined;

    const conversation = await this.context.conversations.get(
      input.conversationId,
    );
    return conversation ? getConversationSpaceId(conversation) : undefined;
  }

  private async loadCandidates(
    query: string,
    limit: number,
    visibilityScope: ContentVisibility | undefined,
  ): Promise<MemoryCandidate[]> {
    const candidateLimit = limit * CANDIDATE_MULTIPLIER;

    if (query.length > 0) {
      const results = await this.context.entityService.search(
        {
          query,
          options: {
            types: MEMORY_ENTITY_TYPES,
            limit: candidateLimit,
            ...(visibilityScope ? { visibilityScope } : {}),
          },
        },
        conversationMemorySearchEntitySchema,
      );
      return results.map((result) => ({
        entity: result.entity,
        score: result.score,
        excerpt: result.excerpt,
      }));
    }

    const entityGroups = await Promise.all(
      MEMORY_ENTITY_TYPES.map((entityType) =>
        this.context.entityService.listEntities(
          {
            entityType,
            options: {
              limit: candidateLimit,
              sortFields: [{ field: "updated", direction: "desc" }],
              ...(visibilityScope ? { filter: { visibilityScope } } : {}),
            },
          },
          conversationMemorySearchEntitySchema,
        ),
      ),
    );

    return entityGroups.flat().map((entity) => ({
      entity,
      score: 0,
      excerpt: buildFallbackExcerpt(entity),
    }));
  }

  private toMemory(candidate: MemoryCandidate): RetrievedConversationMemory {
    const { entity } = candidate;
    const conversation = this.conversationOf(entity);
    if (!conversation) {
      throw new Error(`Memory ${entity.id} has no conversation`);
    }
    const excerpt = candidate.excerpt || buildFallbackExcerpt(entity);
    return {
      id: entity.id,
      entityType: entity.entityType,
      ...conversation,
      updated: entity.updated,
      visibility: entity.visibility,
      score: candidate.score,
      excerpt,
      content: this.buildContextContent(entity, excerpt),
      ...(entity.entityType === SUMMARY_ENTITY_TYPE
        ? {
            messageCount: entity.metadata.messageCount,
            entryCount: summaryAdapter.parseBody(entity.content).entries.length,
          }
        : { status: entity.metadata.status }),
    };
  }

  private matchesIdentity(
    entity: ConversationMemorySearchEntity,
    input: Pick<RetrieveConversationMemoryInput, "identity">,
  ): boolean {
    if (!input.identity) return true;
    return this.identityKeys(entity).includes(actorRefKey(input.identity));
  }

  /** Every actor id a memory names, aliases included. */
  private identityKeys(entity: ConversationMemorySearchEntity): string[] {
    if (this.isSummaryEntity(entity) || this.isDecisionEntity(entity)) {
      const references: Array<{
        identity?: ActorRef | undefined;
        identityAliases?: ActorRef[] | undefined;
      }> = this.isSummaryEntity(entity)
        ? (entity.metadata.participants ?? [])
        : [
            ...(entity.metadata.decidedBy ?? []),
            ...(entity.metadata.mentionedBy ?? []),
          ];
      return references
        .flatMap((reference) => [
          reference.identity,
          ...(reference.identityAliases ?? []),
        ])
        .flatMap((identity) => (identity ? [actorRefKey(identity)] : []));
    }
    return [
      ...(entity.metadata.assignedTo ?? []),
      ...(entity.metadata.requestedBy ?? []),
    ].flatMap((person) => [
      ...(person.id ? [person.id] : []),
      ...(person.aliases ?? []),
    ]);
  }

  private buildContextContent(
    entity: ConversationMemorySearchEntity,
    excerpt: string,
  ): string {
    if (!this.isSummaryEntity(entity)) return excerpt;

    const entries = summaryAdapter.parseBody(entity.content).entries;
    const content = entries
      .slice(0, MAX_SUMMARY_CONTEXT_ENTRIES)
      .map((entry) => {
        const lines = [entry.title, entry.summary.trim()];
        if (entry.keyPoints.length > 0) {
          lines.push(
            ...entry.keyPoints
              .slice(0, MAX_SUMMARY_CONTEXT_KEY_POINTS)
              .map((point) => `- ${point}`),
          );
        }
        return lines.join("\n");
      })
      .join("\n\n");

    return this.truncateContent(
      content || excerpt || buildFallbackExcerpt(entity),
    );
  }

  private truncateContent(content: string): string {
    if (content.length <= MAX_AGENT_CONTEXT_CONTENT_LENGTH) return content;
    const slice = content.slice(0, MAX_AGENT_CONTEXT_CONTENT_LENGTH - 1);
    // Break at the last whitespace so we never cut a word mid-token; fall back
    // to a hard cut only when a single token exceeds the limit.
    const lastBreak = slice.search(/\s\S*$/);
    const truncated = lastBreak > 0 ? slice.slice(0, lastBreak) : slice;
    return `${truncated.trimEnd()}…`;
  }

  private isSummaryEntity(
    entity: ConversationMemorySearchEntity,
  ): entity is SummaryEntity {
    return entity.entityType === SUMMARY_ENTITY_TYPE;
  }

  private isDecisionEntity(
    entity: ConversationMemorySearchEntity,
  ): entity is DecisionEntity {
    return entity.entityType === DECISION_ENTITY_TYPE;
  }

  private getEntitySpaceId(entity: ConversationMemorySearchEntity): string {
    return this.conversationOf(entity)?.spaceId ?? "";
  }

  /** The conversation a memory belongs to; none for an Inbox-tracked action item. */
  private conversationOf(entity: ConversationMemorySearchEntity):
    | {
        conversationId: string;
        spaceId: string;
        channelId: string;
        channelName?: string;
        interfaceType: string;
      }
    | undefined {
    if (entity.entityType === ACTION_ITEM_ENTITY_TYPE) {
      const origin = actionItemOrigin(entity.metadata);
      if (origin.kind !== "conversation") return undefined;
      return {
        conversationId: origin.conversationId,
        spaceId: getConversationSpaceId(origin),
        channelId: origin.channelId,
        ...(origin.channelName ? { channelName: origin.channelName } : {}),
        interfaceType: origin.interfaceType,
      };
    }
    const metadata = entity.metadata;
    return {
      // A summary is its conversation: its id is the conversation id.
      conversationId: this.isSummaryEntity(entity)
        ? entity.id
        : entity.metadata.conversationId,
      spaceId:
        "spaceId" in metadata && typeof metadata.spaceId === "string"
          ? metadata.spaceId
          : getConversationSpaceId(metadata),
      channelId: metadata.channelId,
      ...(metadata.channelName ? { channelName: metadata.channelName } : {}),
      interfaceType: metadata.interfaceType,
    };
  }
}
